import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isBuyListPoKind } from "@threadheads/shared";
import RoleGuard from "@/components/auth/RoleGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ClipboardList,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Ship,
  Lock,
} from "lucide-react";
import { ShopifyConfigurationAlert } from "@/components/alerts/ShopifyConfigurationAlert";
import OnboardingModal from "@/components/onboarding/OnboardingModal";
import { useShopifyConfigAlert } from "@/hooks/useShopifyConfigAlert";
import { useLocations } from "@/features/locations/hooks/useLocations";
import { useSuppliers } from "@/features/suppliers/hooks/useSuppliers";
import { useCurrencyFormatter } from "@/hooks/useCurrencyFormatter";
import { Toaster } from "@/components/ui/toaster";
import { PageErrorBoundary } from "@/components/fallbacks";
import { PageLoader } from "@/components/loading/PageLoader";
import type { PurchaseOrder } from "@/features/purchase-orders";
import { useOrderQueries } from "@/features/purchase-orders/hooks/useOrderQueries";
import { useOrderMutations } from "@/features/purchase-orders/hooks/useOrderMutations";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { formatDay, useDateFnsLocale } from "@/lib/format-dates";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useLocation as useSelectedLocation } from "@/contexts/LocationContext";
import { useRole } from "@/hooks/use-role";
import { useOwnedFloorIds } from "@/features/inventory/hooks/useOwnedFloorIds";
import { PoKindBadge } from "@/features/purchase-orders/components/PoKindBadge";
import {
  floorToday,
  orderAndWaitPoStatus,
  orderAndWaitStatusLabel,
} from "@/features/purchase-orders/lib/order-and-wait";
import { ToBuyTab } from "@/features/purchase-orders/components/ToBuyTab";
import { BackToTopPill } from "@/components/BackToTopPill";
import { PageActionBar, PageActionBarDivider } from "@/components/layout/PageActionBar";
import { STATUS_SELECT_CLASS } from "@/features/purchase-orders/utils/status-style";
import {
  ACTIVE_PURCHASE_ORDER_STATUSES,
  formatStatusLabel,
  getShipmentStatusClassName,
  getValidStatusesForOrder,
  validateGuardedStatusChange,
} from "@/features/purchase-orders/utils/order-status";
import {
  buildPurchaseOrderListSearch,
  parsePurchaseOrderListSearch,
  type PurchaseOrderKindFilter as KindFilter,
  type PurchaseOrderListTab as ListTab,
  type PurchaseOrderSortField as SortField,
  type PurchaseOrderSortOrder as SortOrder,
  type PurchaseOrderStatusFilter as StatusFilter,
} from "@/features/purchase-orders/utils/list-state";

const PAGE_SIZE = 50;

/** BLU-1268: the segmented view pill, as StockHoldBack draws it (brand-purple active). */
const TAB_TRIGGER_CLASS =
  "h-6 px-3 text-xs motion-reduce:transition-none data-[state=active]:bg-threadheads-purple data-[state=active]:text-white data-[state=active]:shadow-sm";

const STATUS_FILTER_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "in_production", label: "In Production" },
  { value: "ready", label: "Ready" },
  { value: "in_transit", label: "In Transit" },
  { value: "partially_received", label: "Partial" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_UPDATE_OPTIONS: PurchaseOrder["status"][] = [
  "submitted",
  "in_production",
  "ready",
  "partially_received",
  "received",
  "cancelled",
];

const getOrderSubtotal = (order: PurchaseOrder): number =>
  (order.items ?? []).reduce(
    (sum, item) => sum + ((item.line_cost_total ?? ((item.unit_cost ?? 0) * item.ordered_quantity)) || 0),
    0
  );

const OrdersList = () => {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currency: orgCurrency } = useCurrencyFormatter();
  const dateLocale = useDateFnsLocale();
  const { updateOrderStatus } = useOrderMutations();
  const { shouldShowAlert } = useShopifyConfigAlert();
  // `allLocations`, not `locations`: this map RESOLVES A NAME for a stored
  // purchase order. A PO raised against a location whose fulfilment has since
  // been delegated is still that PO's location, and the filtered list would
  // render an em-dash where the name belongs (BLU-879 review).
  const { allLocations, isLoading: locationsLoading } = useLocations();

  usePageTitle("Purchase Orders", "Create and manage purchase orders");

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [initialListState] = useState(() =>
    parsePurchaseOrderListSearch(searchParams.toString()),
  );
  // BLU-1268: Orders | To buy. To buy is the buyer's view of one OWNED floor,
  // so it only exists for a viewer who has one.
  const { can } = useRole();
  const { locations: selectableLocations, selectedLocation, locationsListed } = useSelectedLocation();
  const ownedFloorIds = useOwnedFloorIds();
  const toBuyAvailable = can("purchase_orders.view") && ownedFloorIds.length > 0;
  const [tabState, setTab] = useState<ListTab>(initialListState.tab);
  // Fall back to Orders only once the floors are known: an unloaded list is
  // empty too, and must not strip `?tab=to-buy` from a shared link.
  const tab: ListTab = tabState === "to-buy" && locationsListed && !toBuyAvailable ? "orders" : tabState;
  const [kindFilter, setKindFilter] = useState<KindFilter>(initialListState.kindFilter);
  const [toBuyPick, setToBuyPick] = useState<string | null>(initialListState.toBuyLocationId);
  const toBuyLocationId = useMemo(() => {
    if (toBuyPick && ownedFloorIds.includes(toBuyPick)) return toBuyPick;
    if (selectedLocation?.id && ownedFloorIds.includes(selectedLocation.id)) return selectedLocation.id;
    return ownedFloorIds[0] ?? null;
  }, [toBuyPick, ownedFloorIds, selectedLocation?.id]);
  const ownedFloors = useMemo(
    () => selectableLocations.filter((location) => ownedFloorIds.includes(location.id)),
    [selectableLocations, ownedFloorIds],
  );
  const toBuyLocationName = ownedFloors.find((location) => location.id === toBuyLocationId)?.name ?? null;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialListState.statusFilter);
  const [supplierFilter, setSupplierFilter] = useState<string>(initialListState.supplierFilter);
  // BLU-574: default the PO list to the active (in-flight) view.
  const [activeOnly, setActiveOnly] = useState(initialListState.activeOnly);
  const [search, setSearch] = useState(initialListState.search);
  const [sortBy, setSortBy] = useState<SortField>(initialListState.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialListState.sortOrder);
  const [page, setPage] = useState(initialListState.page);
  const [actioningOrderId, setActioningOrderId] = useState<string | null>(null);

  const listSearch = buildPurchaseOrderListSearch({
    tab,
    statusFilter,
    kindFilter,
    // Only a floor the operator picked rides the URL; the default follows the switcher.
    toBuyLocationId: toBuyPick && toBuyPick === toBuyLocationId ? toBuyPick : null,
    supplierFilter,
    activeOnly,
    search,
    sortBy,
    sortOrder,
    page,
  });
  const ordersReturnTo = `/orders${listSearch}`;

  useEffect(() => {
    const currentSearch = searchParams.toString();
    const nextSearch = listSearch.startsWith("?") ? listSearch.slice(1) : listSearch;
    if (currentSearch !== nextSearch) setSearchParams(nextSearch, { replace: true });
  }, [listSearch, searchParams, setSearchParams]);

  const queryStatus = statusFilter === "all" ? undefined : statusFilter;
  const queryStatuses =
    activeOnly && statusFilter === "all" ? ACTIVE_PURCHASE_ORDER_STATUSES : undefined;
  const {
    orders,
    pagination,
    loading: ordersLoading,
    isFetching: ordersFetching,
    error: ordersError,
  } = useOrderQueries({
    page,
    pageSize: PAGE_SIZE,
    // Hard-locked to purchase orders — transfers live at /transfers.
    orderType: "purchase",
    status: queryStatus,
    statuses: queryStatuses,
    supplierId: supplierFilter === "all" ? undefined : supplierFilter,
    kind: kindFilter === "all" ? undefined : kindFilter,
    searchTerm: search,
    sortBy,
    sortOrder,
    enabled: tab === "orders",
  });

  const locationNameById = useMemo(() => {
    return new Map(allLocations.map((location) => [location.id, location.name]));
  }, [allLocations]);
  // BLU-1506: an Order and wait PO is Late against ITS floor's today.
  const floorTodayById = useMemo(() => {
    const now = new Date();
    return new Map(allLocations.map((location) => [location.id, floorToday(location.timezone, now)]));
  }, [allLocations]);
  const toBuyLocationTimezone = allLocations.find((location) => location.id === toBuyLocationId)?.timezone ?? null;

  // Include inactive suppliers so POs raised against a since-deactivated
  // supplier stay filterable; actives sort first, then alphabetical.
  const { suppliers } = useSuppliers({ includeInactive: true });
  const supplierOptions = useMemo(
    () =>
      [...suppliers].sort((a, b) =>
        a.is_active === b.is_active
          ? a.title.localeCompare(b.title)
          : a.is_active
            ? -1
            : 1,
      ),
    [suppliers],
  );

  const handleOnboardingComplete = () => {
    localStorage.setItem("onboarding_completed", "true");
    setShowOnboarding(false);
  };

  const handleCompleteSetup = () => {
    // New-flow orgs (BLU-1376) set up in the wizard, never the old pop-up.
    if (organization?.setup_required === true) {
      navigate("/onboarding");
      return;
    }
    setShowOnboarding(true);
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortOrder("asc");
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    return sortOrder === "asc"
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const totalPages = Math.max(1, pagination.totalPages);
  const safePage = Math.min(page, totalPages);
  const startItem = pagination.total === 0 ? 0 : pagination.offset + 1;
  const endItem = pagination.total === 0 ? 0 : pagination.offset + orders.length;

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const formatMoney = (value?: number | null, currency?: string | null) => {
    if (value === null || value === undefined) return "-";
    const code = (currency ?? orgCurrency).trim().toUpperCase();
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code.length === 3 ? code : "USD",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const justInTimeStatusLabel = (status: PurchaseOrder["status"]) =>
    status === "received"
      ? t("jit.status.completed")
      : status === "cancelled"
        ? t("jit.status.cancelled")
        : t("jit.status.toBuy");

  /**
   * BLU-1506: an Order and wait PO's read-only status, in its own words and
   * the PO palette: Not sent yet (draft slate), On the way (in transit cyan),
   * Late (amber), Arrived (received emerald), Cancelled (red).
   */
  const orderAndWaitListStatus = (order: PurchaseOrder) => {
    const display = orderAndWaitPoStatus(
      order.status,
      order.arrival_eta_date,
      floorTodayById.get(order.location_id ?? "") ?? null,
    );
    const className =
      display === "not_sent"
        ? STATUS_SELECT_CLASS.draft
        : display === "on_the_way"
          ? STATUS_SELECT_CLASS.in_transit
          : display === "late"
            ? STATUS_SELECT_CLASS.submitted
            : display === "arrived"
              ? STATUS_SELECT_CLASS.received
              : STATUS_SELECT_CLASS[order.status];
    return { className, label: orderAndWaitStatusLabel(t, display, { locale: dateLocale }) };
  };

  const runOrderAction = async (orderId: string, fn: () => Promise<void>) => {
    setActioningOrderId(orderId);
    try {
      await fn();
    } finally {
      setActioningOrderId(null);
    }
  };

  const handleGuardedStatusChange = async (
    order: PurchaseOrder,
    newStatus: PurchaseOrder["status"]
  ) => {
    const guard = validateGuardedStatusChange(order, newStatus);
    if (!guard.valid) {
      toast({ title: guard.title, description: guard.description });
      navigate(`/orders/${order.id}`, { state: { ordersReturnTo } });
      return;
    }

    await runOrderAction(order.id, async () => {
      await updateOrderStatus(order.id, newStatus, order.location_id);
    });
  };

  // BLU-1268: Buy today's "show all" lands on the Orders tab, filtered to the
  // open just-in-time POs (status To buy), every page of them.
  const showAllOpenJustInTime = () => {
    setTab("orders");
    setKindFilter("just_in_time");
    setStatusFilter("submitted");
    setActiveOnly(false);
    setPage(1);
  };

  if ((tab === "orders" && ordersLoading) || locationsLoading) {
    return (
      <RoleGuard requirePurchaseOrderAccess>
        <div className="flex flex-col">
          <PageLoader pool="purchaseOrders" />
        </div>
      </RoleGuard>
    );
  }

  if (tab === "orders" && ordersError) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md">
          <h2 className="text-xl font-semibold text-red-700 mb-2">
            Error Loading Data
          </h2>
          <p className="text-red-600">
            {ordersError?.message || "Failed to load data"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8">
          {shouldShowAlert && (
            <div className="mb-6">
              <ShopifyConfigurationAlert onCompleteSetup={handleCompleteSetup} />
            </div>
          )}

          {/* BLU-1268: the Tabs ROOT spans the bar and both panels (the
              StockHoldBack pattern) so each trigger's aria-controls resolves. */}
          <Tabs value={tab} onValueChange={(value) => setTab(value as ListTab)}>
            <PageActionBar
              ariaLabel="Purchase orders actions"
              left={
                <>
                  {toBuyAvailable && (
                    <>
                      <TabsList className="h-8 self-end" aria-label={t("tabs.aria")}>
                        <TabsTrigger value="orders" className={TAB_TRIGGER_CLASS}>
                          {t("tabs.orders")}
                        </TabsTrigger>
                        <TabsTrigger value="to-buy" className={TAB_TRIGGER_CLASS}>
                          {t("tabs.toBuy")}
                        </TabsTrigger>
                      </TabsList>
                      <PageActionBarDivider />
                    </>
                  )}
                  {tab === "to-buy" ? (
                    <Select
                      value={toBuyLocationId ?? undefined}
                      onValueChange={(value) => setToBuyPick(value)}
                    >
                      <SelectTrigger className="h-8 w-[200px] text-xs" aria-label={t("toBuy.floor")}>
                        <SelectValue placeholder={t("toBuy.floor")} />
                      </SelectTrigger>
                      <SelectContent>
                        {ownedFloors.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <>
                      <div className="relative w-56">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search order, supplier, status..."
                          value={search}
                          onChange={(event) => {
                            setSearch(event.target.value);
                            setPage(1);
                          }}
                          className="h-8 pl-9 text-xs"
                        />
                      </div>
                      <Filter className="h-4 w-4 text-muted-foreground self-center" aria-hidden="true" />
                      <Select
                        value={statusFilter}
                        onValueChange={(value) => {
                          setStatusFilter(value as StatusFilter);
                          if (value !== "all") {
                            setActiveOnly(false);
                          }
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[160px] text-xs">
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_FILTER_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={supplierFilter}
                        onValueChange={(value) => {
                          setSupplierFilter(value);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[180px] text-xs">
                          <SelectValue placeholder="All Suppliers" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Suppliers</SelectItem>
                          {supplierOptions.map((supplier) => (
                            <SelectItem key={supplier.id} value={supplier.id}>
                              {supplier.title}
                              {!supplier.is_active && " (inactive)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={kindFilter}
                        onValueChange={(value) => {
                          setKindFilter(value as KindFilter);
                          setPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-[150px] text-xs" aria-label={t("kind.label")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t("kind.all")}</SelectItem>
                          <SelectItem value="standard">{t("kind.standard")}</SelectItem>
                          <SelectItem value="just_in_time">{t("kind.justInTime")}</SelectItem>
                          <SelectItem value="order_and_wait">{t("kind.orderAndWait")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <PageActionBarDivider />
                      <Button
                        variant={activeOnly ? "default" : "outline"}
                        size="sm"
                        className={cn(
                          "h-8 px-3 text-xs gap-1.5 shrink-0",
                          activeOnly && "border-green-600 bg-green-600 text-white hover:border-green-700 hover:bg-green-700"
                        )}
                        onClick={() => {
                          setActiveOnly((prev) => !prev);
                          setStatusFilter("all");
                          setPage(1);
                        }}
                      >
                        Active
                      </Button>
                    </>
                  )}
                </>
              }
              right={
                <Button
                  size="sm"
                  onClick={() => navigate("/orders/new")}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Create Purchase Order
                </Button>
              }
            />

            <TabsContent value="orders" className="mt-0">
              <div className="bg-card rounded-lg shadow border mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <button onClick={() => handleSort("po_number")} className="flex items-center hover:text-foreground">
                          Order # <SortIcon field="po_number" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button onClick={() => handleSort("supplier_name")} className="flex items-center hover:text-foreground">
                          Supplier <SortIcon field="supplier_name" />
                        </button>
                      </TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>
                        <button onClick={() => handleSort("status")} className="flex items-center hover:text-foreground">
                          Status <SortIcon field="status" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button onClick={() => handleSort("created_at")} className="flex items-center hover:text-foreground">
                          Created <SortIcon field="created_at" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button onClick={() => handleSort("submitted_date")} className="flex items-center hover:text-foreground">
                          Submitted <SortIcon field="submitted_date" />
                        </button>
                      </TableHead>
                      {/* PR #1989 review: eta_date is the Ready ETA; the detail page and create form use the same name. */}
                      <TableHead className="whitespace-nowrap">Ready ETA</TableHead>
                      <TableHead className="text-right">
                        <button onClick={() => handleSort("total_cost")} className="flex items-center justify-end hover:text-foreground w-full">
                          Total <SortIcon field="total_cost" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <div className="flex items-center">
                          <Ship className="h-4 w-4 mr-1" />
                          Shipment
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          {activeOnly ? (
                            // BLU-574: default active view hid these — offer a one-click way back to all.
                            <div className="flex flex-col items-center gap-2">
                              <span className="text-muted-foreground">No active orders</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-xs"
                                onClick={() => {
                                  setActiveOnly(false);
                                  setPage(1);
                                }}
                              >
                                Show all orders
                              </Button>
                            </div>
                          ) : search.trim() || statusFilter !== "all" || kindFilter !== "all" ? (
                            "No matching orders found"
                          ) : (
                            "No orders found"
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      orders.map((order) => {
                        // BLU-377: status is independent of payment now — paid POs can still
                        // have their fulfillment status changed (rate stays locked via DB trigger).
                        // BLU-1268: a just-in-time PO only completes (on its page) or
                        // cancels, so its status is read-only here, in its own words. An
                        // order-and-wait PO is read-only too, but wears no just-in-time words.
                        const isJustInTime = order.kind === "just_in_time";
                        const orderAndWait = order.kind === "order_and_wait" ? orderAndWaitListStatus(order) : null;
                        const showStatusSelect =
                          !isBuyListPoKind(order.kind) &&
                          order.status !== "draft" &&
                          order.status !== "cancelled" &&
                          order.status !== "in_transit";
                        const isMutating = actioningOrderId === order.id;
                        const validStatuses = getValidStatusesForOrder(order, STATUS_UPDATE_OPTIONS);

                        return (
                          <TableRow
                            key={order.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/orders/${order.id}`, { state: { ordersReturnTo } })}
                          >
                            <TableCell>
                              <Link
                                to={`/orders/${order.id}`}
                                state={{ ordersReturnTo }}
                                className="group block w-fit max-w-full"
                                onClick={(event) => event.stopPropagation()}
                              >
                                <span className="flex flex-wrap items-center gap-1.5">
                                  <span className="font-medium text-primary group-hover:underline">
                                    {order.po_number}
                                  </span>
                                  {isBuyListPoKind(order.kind) && <PoKindBadge kind={order.kind} />}
                                </span>
                                {order.title ? (
                                  <div className="max-w-[220px] truncate text-xs text-muted-foreground">
                                    {order.title}
                                  </div>
                                ) : null}
                              </Link>
                            </TableCell>
                            <TableCell>
                              {order.supplier_name || "-"}
                            </TableCell>
                            <TableCell>{locationNameById.get(order.location_id ?? "") || "-"}</TableCell>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              {showStatusSelect ? (
                                <Select
                                  value={order.status}
                                  onValueChange={(value) =>
                                    void handleGuardedStatusChange(order, value as PurchaseOrder["status"])
                                  }
                                  disabled={isMutating}
                                >
                                  <SelectTrigger
                                    className={cn(
                                      "w-[145px] h-8 border-0 text-[11px] font-medium",
                                      STATUS_SELECT_CLASS[order.status]
                                    )}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {validStatuses.map((status) => (
                                      <SelectItem key={status} value={status} className="text-[11px]">
                                        {formatStatusLabel(status)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <div
                                  className={cn(
                                    "w-[145px] h-8 rounded-md px-3 text-[11px] font-medium inline-flex items-center",
                                    orderAndWait?.className ?? STATUS_SELECT_CLASS[order.status]
                                  )}
                                >
                                  {orderAndWait
                                    ? orderAndWait.label
                                    : isJustInTime
                                      ? justInTimeStatusLabel(order.status)
                                      : formatStatusLabel(order.status)}
                                </div>
                              )}
                              {/* BLU-377: payment indicator — small, inline, only when paid */}
                              {order.payment_status === "paid" && (
                                <Badge
                                  variant="paid"
                                  size="sm"
                                  className="ml-1 h-5 inline-flex items-center gap-0.5 px-1.5"
                                  title="Paid (exchange rate locked)"
                                >
                                  <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                                  PAID
                                </Badge>
                              )}
                            </TableCell>
                            {/* PR #1989 review: all three dates share formatDay so a row reads one format. eta_date is
                                a calendar DATE; formatDay parses it as local midnight, so it shows on its own day in every
                                zone (new Date() reads it as UTC midnight, a day early west of UTC). */}
                            <TableCell className="whitespace-nowrap">
                              {order.created_at ? formatDay(order.created_at, dateLocale) : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {order.submitted_date ? formatDay(order.submitted_date, dateLocale) : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {order.eta_date ? formatDay(order.eta_date, dateLocale) : "-"}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatMoney(order.total_cost ?? getOrderSubtotal(order), order.currency ?? null)}
                            </TableCell>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              {(order.shipments ?? []).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {(order.shipments ?? []).map((shipment) => (
                                    <Badge
                                      key={shipment.id}
                                      className={cn(
                                        "cursor-pointer text-xs",
                                        getShipmentStatusClassName(shipment.status)
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/shipments/${shipment.id}`);
                                      }}
                                    >
                                      {shipment.shipment_number ?? "Shipment"}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>

                {pagination.total > PAGE_SIZE ? (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {startItem} to {endItem} of {pagination.total} orders
                    </p>
                    <div className="flex space-x-2">
                      <span className="inline-flex items-center px-2 text-sm text-muted-foreground">
                        Page {safePage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safePage <= 1 || ordersFetching}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safePage >= totalPages || ordersFetching}
                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </TabsContent>
            {toBuyAvailable && (
              <TabsContent value="to-buy" className="mt-0">
                <ToBuyTab
                  locationId={toBuyLocationId}
                  locationName={toBuyLocationName}
                  locationTimezone={toBuyLocationTimezone}
                  ordersReturnTo={ordersReturnTo}
                  onShowAllOpen={showAllOpenJustInTime}
                />
              </TabsContent>
            )}
          </Tabs>
        </main>

        <Toaster />

        <OnboardingModal
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
        />

        <BackToTopPill />
      </div>
    </RoleGuard>
  );
};

export default function OrdersListWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <OrdersList />
    </PageErrorBoundary>
  );
}
